import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import {
  ExecuteSelectLoadingPage,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/ExecuteSelectLoadingPage';
import {
  FDBox,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import {
  FadeInBox,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/FadeInBox';
import React from 'react';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import fr from 'facet-rest';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { metaOverrideSet as ScenarioSelectionMetaOverrideSet } from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/ScenarioSelectionRedefinedStepLayoutWithContentHeader/metaOverrideSet';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import { useRightColumnContext } from 'Context/RightColumnContext';
import { PlannerInsightChatLayout } from 'Views/Page/Content/Templates/WizardTemplates/RoadmapDeliverable/PlannerInsightChat/layout';
import PlannerInsightChatMetaOverrideSet from 'Views/Page/Content/Templates/WizardTemplates/RoadmapDeliverable/PlannerInsightChat/metaOverrideSet';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useEventTrackingLogEvent, { categories, eventTypes, eventSubTypes } from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import { RoadmapExecutiveSummaryNextUp } from '../RoadmapExecutiveSummary/RoadmapExecutiveSummaryNextUp/RoadmapExecutiveSummaryNextUp';
import RoadmapExecutiveSummaryNextUpMetaOverrideSet from '../RoadmapExecutiveSummary/RoadmapExecutiveSummaryNextUp/metaOverrideSet';
import { RoadmapExecutiveSummaryActions } from '../RoadmapExecutiveSummary/RoadmapExecutiveSummaryActions/RoadmapExecutiveSummaryActions';
import RoadmapExecutiveSummaryActionsMetaOverrideSet from '../RoadmapExecutiveSummary/RoadmapExecutiveSummaryActions/metaOverrideSet';

const ROADMAP_EXECUTIVE_SUMMARY_GOAL_OVERVIEW_SECTION_ID = 'roadmap-executive-summary-goal-overview-section';
const ROADMAP_EXECUTIVE_SUMMARY_NEXT_UP_SECTION_ID = 'roadmap-executive-summary-next-up-section';
const ROADMAP_EXECUTIVE_SUMMARY_ACTIONS_SECTION_ID = 'roadmap-executive-summary-actions-section';

// Keep in mind when editing this component that this is used both on the intro page (both for the agent and client)
// and in the decision step within the scenario selection wizard.
export const RoadmapExecutiveSummaryContentForDeliverable = ({ resource, showHeaderAndFooter = true }) => {
  const [loading, setLoading] = React.useState(false);
  const { displayErrorMessage } = useSnackbarContext();
  const { setPageContentCollection } = usePageContentContext();
  const { logStandardizedEvent, logStandardizedEventFailureWithError } = useEventTrackingLogEvent();
  const { isAgent, userResource } = useUserContext();
  useBoneBackgroundColor();

  const { loadRightColumnCollectionLink, rightColumnCollectionLoading } = useRightColumnContext();
  const { isSelecting } = useSelectScenarioContext();
  const isMobile = useMobileLayout();

  if (!resource) {
    return null;
  }

  const eventProperties = {
    resourceType: resource?.type(),
    isAgent,
    userId: userResource?.id(),
  };

  const handleNext = async () => {
    logStandardizedEvent(categories.roadmapDeliverable, eventTypes.clickButton, eventSubTypes.submitStep, { eventProperties });
    try {
      setLoading(true);
      const nextStepColl = await resource.submit();
      logStandardizedEvent(categories.roadmapDeliverable, eventTypes.clickButton, eventSubTypes.success, { eventProperties });
      setPageContentCollection(nextStepColl);
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to continue. Please refresh and try again.');
      logStandardizedEventFailureWithError(categories.roadmapDeliverable, eventTypes.clickButton, eventSubTypes.failure, err, { eventProperties });
    } finally {
      setLoading(false);
    }
  };


  const portfolioRel = resource.withRelationship(
    fr.relationships.portfolio.name,
    (rel) => rel,
    () => null,
  );

  const overviewSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_GOAL_OVERVIEW_SECTION_ID);
  const nextUpSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_NEXT_UP_SECTION_ID);
  const actionsSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_ACTIONS_SECTION_ID);

  if (resource.isTruthy('shouldExecuteSelect') && isSelecting) {
    return <ExecuteSelectLoadingPage resource={resource} />;
  }

  const PlannerInsightChat = (
    <MetaResourceRenderer
      resource={resource} // has attrs for cfp insight
      layout={PlannerInsightChatLayout}
      metaOverrideSet={PlannerInsightChatMetaOverrideSet}
    />
  );

  const ActionsSectionComponent = actionsSection ? (
    <MetaResourceRenderer
      resource={actionsSection}
      layout={RoadmapExecutiveSummaryActions}
      metaOverrideSet={RoadmapExecutiveSummaryActionsMetaOverrideSet}
      LayoutProps={{
        portfolioRel,
        isRoadmapDeliverable: true,
        setLoading,
      }}
    />
  ) : null;

  const NextUpSectionComponent = nextUpSection ? (
    <MetaResourceRenderer
      resource={nextUpSection}
      layout={RoadmapExecutiveSummaryNextUp}
      metaOverrideSet={RoadmapExecutiveSummaryNextUpMetaOverrideSet}
      LayoutProps={{
        ActionsSectionComponent,
        isRoadmapDeliverable: true,
      }}
    />
  ) : null;

  const MessagePlannerButtonComponent = overviewSection.withRelationship(fr.relationships.messagePlannerFromCustomInsight.name,
    (rel) => (
      <FDBox minWidth={160} maxWidth={isMobile ? undefined : 240} width={'100%'}>
        <FDSecondaryActionButton
          disabled={!!rel.meta('disabled')}
          label={rel.meta('prompt')}
          loading={!!rightColumnCollectionLoading}
          onClick={() => {
            logStandardizedEvent(categories.roadmapDeliverable, eventTypes.clickButton, 'open message planner drawer', { eventProperties });
            loadRightColumnCollectionLink(rel.link('related'));
          }}
          isFullWidth
          {...dataAttrs('cfp-insight-deliverable-message-planner-button')}
        />
      </FDBox>
    ),
    () => null);

  const footer = (
    <FDStack direction={'column'} spacing={2} alignItems={'center'} width={'100%'}>
      <MetaControl attrName={'disclaimerText'} resource={actionsSection} metaOverrideSet={ScenarioSelectionMetaOverrideSet} />
      <MetaControl attrName={'disclaimerText'} resource={resource} metaOverrideSet={ScenarioSelectionMetaOverrideSet} />
    </FDStack>
  );


  let paddingY = isMobile ? 4 : 2;
  if (showHeaderAndFooter && !isMobile) {
    paddingY = 8;
  }

  const submitBtn = (
    <FDBox minWidth={160} maxWidth={isMobile ? undefined : 240} width={'100%'}>
      <FDPrimaryActionButton
        onClick={handleNext}
        label={resource.attr('primaryActionLabel') || 'Continue'}
        loading={loading}
        isFullWidth
        {...dataAttrs('continue-btn')}
      />
    </FDBox>
  );

  // directions and button orders are swapped for mobile
  const buttons = isMobile ? (
    <FDStack direction={'column'} spacing={2} alignItems={'center'} justifyContent={'center'} width={'100%'}>
      {submitBtn}
      {MessagePlannerButtonComponent}
    </FDStack>
  ) : (
    <FDStack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'center'} width={'100%'}>
      {MessagePlannerButtonComponent}
      {submitBtn}
    </FDStack>
  );

  return (
    <FDStack direction={'column'} paddingY={paddingY} spacing={1} width={'100%'} maxWidth={600}>
      <FadeInBox>
        {PlannerInsightChat}
        <FDStack direction={'column'} spacing={12}>
          <FDStack direction={'column'} paddingY={paddingY} spacing={3}>
            {NextUpSectionComponent}
            {buttons}
          </FDStack>
        </FDStack>
        {footer}
      </FadeInBox>
    </FDStack>
  );
};
